import { GridLayoutItem } from '@progress/kendo-react-layout';
import { CustomgridLayoutWrapper } from 'admin/components/wrappers';
import AdminDropDown from 'app/components/AdminDropDown';
import { AdminTextField } from 'app/components/AdminTextField';
import { ProfileImageUploadCard } from 'app/components/ProfileImageUploadCard';
import styled from 'styled-components';
import React, { Dispatch, MutableRefObject, SetStateAction } from 'react';
import SimpleReactValidator from 'simple-react-validator';
import {
  GeneralLookup,
  GeneralLookupWrapper,
  User,
  UserGroupLookup,
  UserRoles,
} from 'services/openapi';
import { getLookupArray } from 'utils/common';
import { DropDownModel } from 'app/components/SHdropdown';

type Props = {
  action?: string | undefined;
  simpleValidator: MutableRefObject<SimpleReactValidator>;
  setUserPayload: Dispatch<SetStateAction<User>>;
  userPayload: User;
  setImageUploadBlobPayload?: Dispatch<SetStateAction<Blob | null>>;
  setTeamDefault: Dispatch<SetStateAction<GeneralLookup | undefined>>;
  teamDefault: GeneralLookup | undefined;
  imgSrc: string | null;
  setImgSrc: Dispatch<SetStateAction<string | null>>;
  setUserPreDataList: Dispatch<SetStateAction<DropDownModel[]>>;
  userPreDataList: DropDownModel[];
  loading: boolean;
  lookUps: GeneralLookupWrapper[];
  permissionRoleData: UserRoles[];
  setRoleValue: React.Dispatch<React.SetStateAction<GeneralLookup | undefined>>;
  roleValue: GeneralLookup | undefined;
  userTypeLookUps: UserGroupLookup[];
  getRolePermissionsList: (
    roleId?: string,
    isUserUpdate?: boolean,
    userId?: string,
  ) => void;
  setUserTypeValue: React.Dispatch<
    React.SetStateAction<GeneralLookup | undefined>
  >;
  userTypeValue: GeneralLookup | undefined;
};

export const UserInfoFormBlock = ({
  action,
  simpleValidator,
  setUserPayload,
  userPayload,
  setImageUploadBlobPayload,
  setTeamDefault,
  teamDefault,
  imgSrc,
  setImgSrc,
  setUserPreDataList,
  userPreDataList,
  loading,
  lookUps,
  permissionRoleData,
  setRoleValue,
  roleValue,
  userTypeLookUps,
  getRolePermissionsList,
  setUserTypeValue,
  userTypeValue,
}: Props) => {
  const [forceUpdate, setForceUpdate] = React.useState(false);
  const roleData: GeneralLookup[] = permissionRoleData.map(item => {
    return {
      id: item.roleId as string,
      name: item.normalizedName as string,
    };
  });
  const userTypeData: GeneralLookup[] = userTypeLookUps.map(item => {
    return {
      id: item.id.toString(),
      name: item.name as string,
    };
  });
  const isTeamDefault = userPreDataList.find(item => item.id === 2);
  const handleChange = event => {
    const { name, value } = event.target;
    setUserPayload(prevData => ({
      ...prevData,
      [name]: value,
    }));
  };
  const handleDropdownChange = (
    data: DropDownModel | GeneralLookup,
    name: string,
  ) => {
    setUserPayload(prevData => ({
      ...prevData,
      [name]: data,
    }));
  };
  const handleUserTypeChange = (data: GeneralLookup) => {
    setUserTypeValue(data);
    delete simpleValidator.current.fields['Role'];
    delete simpleValidator.current.messagesShown['Role']; // This clears the error message
    delete simpleValidator.current.errorMessages['Role'];
    delete simpleValidator.current.errorMessages['Team'];
    delete simpleValidator.current.fields['Team'];
    delete simpleValidator.current.messagesShown['Team'];
    setForceUpdate(prev => !prev);
    if (data.id === '3') {
      const foundUserType = userTypeLookUps.find(item => item.id === 3);
      const defaultRole = foundUserType?.children?.[0] || {
        id: '',
        name: '',
      };
      setRoleValue(defaultRole);
      getRolePermissionsList(defaultRole.id);
      setUserPayload(prevData => ({
        ...prevData,
        userType: data.name,
        userGroupType: 3,
      }));
    } else {
      setRoleValue(undefined);
      setUserPayload(prevData => ({
        ...prevData,
        userGroupType: 1,
      }));
      setTeamDefault(undefined);
    }
  };
  const handleRoleChange = (data: GeneralLookup) => {
    setRoleValue(data);
    setUserPayload(prevData => ({
      ...prevData,
      userType: data.name,
    }));
  };
  const handleTeamChange = (data: GeneralLookup, name: string) => {
    setUserPayload(prevData => ({
      ...prevData,
      [name]: data.id,
    }));
    setTeamDefault(data);
  };
  const handleImageSelect = e => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        setImageUploadBlobPayload?.(file);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  return (
    <>
      <GridWrapper>
        <CustomgridLayoutWrapper
          rows={[{ height: 'auto' }]}
          cols={[
            { width: 'calc(32.33% - 14px)' },
            { width: 'calc(68.67% - 14px)' },
          ]}
          gap={{ rows: 20, cols: 30 }}
        >
          <GridLayoutItem>
            <ProfileImageUploadCard
              uploadButtonLabel={`${imgSrc ? 'Change Image' : 'Upload Image'}`}
              uploadButtonType="uploadFile"
              imgSrc={imgSrc}
              setImgSrc={setImgSrc}
              onSelectFile={handleImageSelect}
              isLoader={loading}
            />
          </GridLayoutItem>
          <GridLayoutItem>
            <CustomgridLayoutWrapper
              rows={[{ height: 'auto' }]}
              cols={[
                { width: 'calc(50% - 14px)' },
                { width: 'calc(50% - 14px)' },
              ]}
              gap={{ rows: 20, cols: 10 }}
            >
              <GridLayoutItem>
                <AdminDropDown
                  label="User Type"
                  placeholder=""
                  data={userTypeData}
                  className={'MHMultiCheck'}
                  validations="required"
                  validator={simpleValidator.current}
                  isLoader={loading}
                  onDataChange={data => handleUserTypeChange(data)}
                  defaultValue={userTypeValue}
                />
              </GridLayoutItem>
              {userTypeValue?.id === '1' && (
                <GridLayoutItem>
                  <AdminDropDown
                    label="Role"
                    placeholder=""
                    data={roleData}
                    className={'MHMultiCheck'}
                    validations="required"
                    validator={simpleValidator.current}
                    isLoader={loading}
                    onDataChange={data => handleRoleChange(data)}
                    defaultValue={roleValue}
                  />
                </GridLayoutItem>
              )}
              {userTypeValue?.id === '3' && (
                <GridLayoutItem>
                  <AdminDropDown
                    label="Team"
                    placeholder=""
                    onDataChange={data => handleTeamChange(data, 'teamId')}
                    data={getLookupArray('TEAM', lookUps)}
                    defaultValue={teamDefault}
                    className="field-style"
                    validations="required"
                    validator={simpleValidator.current}
                    isLoader={loading}
                  />
                </GridLayoutItem>
              )}
              <GridLayoutItem>
                <AdminTextField
                  label="User First Name"
                  placeholder=""
                  value={userPayload.firstName ?? ''}
                  onChange={handleChange}
                  name="firstName"
                  validations="required"
                  validator={simpleValidator.current}
                  className="field-style"
                  isLoader={loading}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <AdminTextField
                  label="User Last Name"
                  placeholder=""
                  value={userPayload.lastName ?? ''}
                  onChange={handleChange}
                  name="lastName"
                  validations="required"
                  validator={simpleValidator.current}
                  className="field-style"
                  isLoader={loading}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <AdminTextField
                  label="User Name"
                  placeholder=""
                  value={userPayload.userName ?? ''}
                  onChange={handleChange}
                  name="userName"
                  validations="required"
                  validator={simpleValidator.current}
                  className="field-style"
                  isLoader={loading}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <AdminTextField
                  label="Phone Number"
                  placeholder=""
                  value={userPayload.primaryPhone ?? ''}
                  onChange={e => {
                    const inputValue = e.target.value;
                    if (inputValue.length <= 10 && /^\d*$/.test(inputValue)) {
                      setUserPayload?.({
                        ...userPayload,
                        primaryPhone: inputValue,
                      });
                    }
                  }}
                  name="primaryPhone"
                  validations="required"
                  validator={simpleValidator.current}
                  className="field-style"
                  isLoader={loading}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <AdminTextField
                  label="User Email"
                  placeholder=""
                  value={userPayload.email ?? ''}
                  onChange={handleChange}
                  name="email"
                  validations="required|email"
                  validator={simpleValidator.current}
                  className="field-style"
                  isLoader={loading}
                />
              </GridLayoutItem>
            </CustomgridLayoutWrapper>
          </GridLayoutItem>
        </CustomgridLayoutWrapper>
        <CustomgridLayoutWrapper
          rows={[{ height: 'auto' }]}
          cols={[
            { width: 'calc(33.33% - 8px)' },
            { width: 'calc(33.33% - 6px)' },
            { width: 'calc(33.33% - 9px)' },
          ]}
          gap={{ rows: 20, cols: 10 }}
          style={{ paddingTop: '20px' }}
        >
          <GridLayoutItem>
            <AdminTextField
              label="City"
              placeholder=""
              value={userPayload.city ?? ''}
              onChange={handleChange}
              name="city"
              validations="required"
              validator={simpleValidator.current}
              className="field-style"
              isLoader={loading}
            />
          </GridLayoutItem>
          <GridLayoutItem>
            <AdminDropDown
              label="State"
              placeholder=""
              onDataChange={data => handleDropdownChange(data, 'state')}
              data={getLookupArray('STATE', lookUps)}
              defaultValue={userPayload.state}
              validations="required"
              validator={simpleValidator.current}
              className="field-style"
              isLoader={loading}
            />
          </GridLayoutItem>
          <GridLayoutItem>
            <AdminTextField
              label="Zip"
              placeholder=""
              value={userPayload.zip ?? ''}
              onChange={handleChange}
              name="zip"
              validations={'required|max:10'}
              validator={simpleValidator.current}
              className="field-style"
              isLoader={loading}
            />
          </GridLayoutItem>
          <GridLayoutItem>
            <AdminTextField
              label="Address"
              placeholder=""
              value={userPayload.street ?? ''}
              onChange={handleChange}
              name="street"
              validations={'required|max:300'}
              validator={simpleValidator.current}
              className="field-style"
              isLoader={loading}
            />
          </GridLayoutItem>

        </CustomgridLayoutWrapper>
      </GridWrapper>
    </>
  );
};

const GridWrapper = styled.div`
  padding: 10px 0 20px;
  .k-grid-layout {
    .checkbox {
      margin-left: 15px;
      margin-top: 30px;
      label {
        color: #a3a3a3;
      }
      @media (min-width: 100px) and (max-width: 1365px) {
        margin-left: 5px;
        margin-right: unset;
      }
    }
  }
`;
